import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionConflict, setSessionConflict] = useState(null); // { newUserName, newUserRole }

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const userData = localStorage.getItem('user');

    if (token && refreshToken && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Detect when another tab logs in as a different user
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key !== 'user') return;

      // Another tab cleared storage (logout) — nothing to conflict with
      if (!e.newValue) return;

      try {
        const newUser = JSON.parse(e.newValue);
        // Compare against current tab's user (captured via closure ref)
        setUser(currentUser => {
          if (currentUser && newUser.userid !== currentUser.userid) {
            // A different user has taken over localStorage in another tab
            const roleLabel = {
              admin: 'Admin',
              sr_inspector: 'Reviewer',
              jr_inspector: 'Inspector'
            }[newUser.role] || newUser.role;
            setSessionConflict({ newUserName: newUser.full_name, newUserRole: roleLabel });
          }
          return currentUser; // Don't change our own state
        });
      } catch {
        // ignore parse errors
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = (token, refreshToken, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setSessionConflict(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  // Used by the conflict overlay — clears only React state, does NOT touch
  // localStorage so the other tab's active session is not disrupted.
  const dismissConflict = () => {
    setUser(null);
    setIsAuthenticated(false);
    setSessionConflict(null);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
    sessionConflict,
    dismissConflict
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
